import { useState, useEffect, useRef } from "react";
import { api, getStatusInfo, formatDate, DOCUMENT_CATEGORIES } from "../lib/utils";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { GapReportPanel } from "../components/AIInsightsPanels";
import WrittenInterviewSection from "../components/WrittenInterviewSection";
import CandidateGuide from "../components/CandidateGuide";
import CredentialCard from "../components/CredentialCard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  FileText,
  Upload,
  Clock,
  CheckCircle,
  AlertCircle,
  Building2,
  GraduationCap,
  Award,
  FileCheck,
  Loader2,
  Download,
  LogOut,
  User,
  ChevronRight,
  Star,
  Calendar,
  Video,
  MessageSquare,
  XCircle,
  ClipboardList,
  Lock,
  Trash2,
} from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import ProfileCompletionForm from "../components/ProfileCompletionForm";

// Status flow for progress tracking
const WORKFLOW_STAGES = [
  { key: "pending_approval", label: "Pending Approval", icon: Clock },
  { key: "draft", label: "Application Created", icon: FileText },
  { key: "profile_complete", label: "Profile Complete", icon: ClipboardList },
  { key: "documents_uploaded", label: "Documents Uploaded", icon: Upload },
  { key: "ai_analyzed", label: "AI Analysis Complete", icon: Star },
  { key: "assessor_review", label: "Assessor Review", icon: User },
  { key: "interview_scheduled", label: "Interview Scheduled", icon: Calendar },
  { key: "interview_pending", label: "Interview Pending", icon: Video },
  { key: "iqa_review", label: "IQA Verification", icon: FileCheck },
  { key: "approved", label: "Approved", icon: CheckCircle },
];

export default function CandidateDashboard({ user, onLogout }) {
  const [applicationData, setApplicationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("");
  const [customCategoryText, setCustomCategoryText] = useState("");
  const fileInputRef = useRef(null);
  
  // Interview states
  const [selectingSlot, setSelectingSlot] = useState(false);
  const [appealDialogOpen, setAppealDialogOpen] = useState(false);
  const [appealRemarks, setAppealRemarks] = useState("");
  const [submittingAppeal, setSubmittingAppeal] = useState(false);
  const [infoResponseText, setInfoResponseText] = useState({});
  const [submittingResponse, setSubmittingResponse] = useState(null);

  useEffect(() => {
    fetchApplication();
  }, []);

  const fetchApplication = async () => {
    try {
      const response = await api.get("/my-application");
      setApplicationData(response.data);
    } catch (error) {
      console.error("Failed to load application:", error);
      toast.error("Failed to load your application");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectInterviewSlot = async (slotNumber) => {
    setSelectingSlot(true);
    try {
      await api.post(`/candidates/${applicationData.candidate.candidate_id}/select-interview-slot`, {
        selected_slot: slotNumber
      });
      toast.success("Interview time confirmed!");
      fetchApplication();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to select time slot");
    } finally {
      setSelectingSlot(false);
    }
  };

  const handleSubmitAppeal = async () => {
    if (!appealRemarks.trim()) {
      toast.error("Please provide your statement");
      return;
    }
    
    setSubmittingAppeal(true);
    try {
      await api.post(`/candidates/${applicationData.candidate.candidate_id}/appeal`, {
        remarks: appealRemarks
      });
      toast.success("Appeal submitted successfully");
      setAppealDialogOpen(false);
      setAppealRemarks("");
      fetchApplication();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit appeal");
    } finally {
      setSubmittingAppeal(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadCategory) {
      toast.error("Please select a file and category");
      return;
    }

    if (uploadCategory === "Other Supporting Evidence" && !customCategoryText.trim()) {
      toast.error("Please specify the document type");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      let url = `/candidates/${applicationData.candidate.candidate_id}/documents?category=${encodeURIComponent(uploadCategory)}`;
      if (uploadCategory === "Other Supporting Evidence" && customCategoryText.trim()) {
        url += `&custom_category=${encodeURIComponent(customCategoryText.trim())}`;
      }
      
      await api.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded! AI analysis will be conducted by the assessment team shortly.");
      setUploadDialogOpen(false);
      setUploadCategory("");
      setCustomCategoryText("");
      fetchApplication();
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId, fileName) => {
    if (!window.confirm(`Delete "${fileName}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/documents/${documentId}`);
      toast.success("Document deleted");
      fetchApplication();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete document");
    }
  };

  const handleRespondToInfoRequest = async (requestId) => {
    const text = infoResponseText[requestId]?.trim();
    if (!text) {
      toast.error("Please type your response");
      return;
    }
    setSubmittingResponse(requestId);
    try {
      await api.post(`/candidates/${applicationData.candidate.candidate_id}/respond-info-request`, {
        request_id: requestId,
        response_text: text,
      });
      toast.success("Response submitted");
      setInfoResponseText({ ...infoResponseText, [requestId]: "" });
      fetchApplication();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit response");
    } finally {
      setSubmittingResponse(null);
    }
  };

  const getEffectiveStatus = () => {
    if (!applicationData?.candidate) return null;
    const status = applicationData.candidate.case_status;
    const profileDone = applicationData.candidate.profile_completed;
    // Map statuses to workflow stages
    if (status === "draft" && profileDone) return "profile_complete";
    if (status === "report_generated" || status === "closed") return "approved";
    if (status === "ai_review") return "ai_analyzed";
    return status;
  };

  const getProgressPercentage = () => {
    const effectiveStatus = getEffectiveStatus();
    if (!effectiveStatus) return 0;
    const stageIndex = WORKFLOW_STAGES.findIndex(s => s.key === effectiveStatus);
    if (stageIndex === -1) return 0;
    return Math.round(((stageIndex + 1) / WORKFLOW_STAGES.length) * 100);
  };

  const getCurrentStageIndex = () => {
    const effectiveStatus = getEffectiveStatus();
    if (!effectiveStatus) return -1;
    return WORKFLOW_STAGES.findIndex(s => s.key === effectiveStatus);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!applicationData?.candidate) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">No Application Found</h2>
            <p className="text-slate-500 mb-4">
              Your account is not linked to any RPL application yet.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => window.location.href = '/apply'}>
                Apply as Candidate
              </Button>
              <Button onClick={onLogout} variant="outline">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show pending approval state
  if (applicationData?.candidate?.case_status === "pending_approval") {
    const { candidate } = applicationData;
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900">Credentia AI</h1>
                <p className="text-xs text-slate-500">Candidate Portal</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <CandidateGuide />
              <Button variant="ghost" size="sm" onClick={onLogout}>
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 py-12">
          <Card className="border-amber-200">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Application Pending Review
              </h2>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                Thank you for submitting your application, {candidate.full_name}! 
                An administrator will review your application and assign you to an institution.
              </p>

              <div className="bg-slate-50 rounded-lg p-6 text-left mb-6">
                <h3 className="font-semibold text-slate-900 mb-4">Application Summary</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Full Name</p>
                    <p className="font-medium">{candidate.full_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Email</p>
                    <p className="font-medium">{candidate.email}</p>
                  </div>
                  {candidate.phone && (
                    <div>
                      <p className="text-slate-500">Phone</p>
                      <p className="font-medium">{candidate.phone}</p>
                    </div>
                  )}
                  {candidate.nationality && (
                    <div>
                      <p className="text-slate-500">Nationality</p>
                      <p className="font-medium">{candidate.nationality}</p>
                    </div>
                  )}
                  {candidate.current_employer && (
                    <div>
                      <p className="text-slate-500">Current Employer</p>
                      <p className="font-medium">{candidate.current_employer}</p>
                    </div>
                  )}
                  {candidate.current_role && (
                    <div>
                      <p className="text-slate-500">Current Role</p>
                      <p className="font-medium">{candidate.current_role}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-slate-500">Submitted</p>
                    <p className="font-medium">{formatDate(candidate.created_at)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-left">
                    <p className="font-medium text-blue-800">What happens next?</p>
                    <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                      <li>An administrator will review your application</li>
                      <li>You'll be assigned to an institution and IPCB board</li>
                      <li>You'll receive an email notification when approved</li>
                      <li>After approval, complete your profile (employment, training, history)</li>
                      <li>Once your profile is complete, you can upload your documents</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const { candidate, documents, institution, ipcb } = applicationData;
  const statusInfo = getStatusInfo(candidate.case_status);
  const currentStageIndex = getCurrentStageIndex();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900">Credentia AI</h1>
              <p className="text-xs text-slate-500">Candidate Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-slate-900">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
            <CandidateGuide />
            <Button variant="ghost" size="sm" onClick={onLogout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Welcome Card */}
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">
                  Welcome, {candidate.full_name}!
                </h2>
                <p className="text-slate-600 mt-1">
                  Track your RPL assessment progress below.
                </p>
              </div>
              <Badge className={`${statusInfo.color} text-sm px-3 py-1`}>
                {statusInfo.label}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Progress Tracker */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Assessment Progress
            </CardTitle>
            <CardDescription>Your application is {getProgressPercentage()}% complete</CardDescription>
          </CardHeader>
          <CardContent>
            <Progress value={getProgressPercentage()} className="mb-6" />
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              {WORKFLOW_STAGES.map((stage, index) => {
                const Icon = stage.icon;
                const isApproved = currentStageIndex === WORKFLOW_STAGES.length - 1;
                const isCompleted = index < currentStageIndex || isApproved;
                const isCurrent = index === currentStageIndex && !isApproved;
                
                return (
                  <div 
                    key={stage.key}
                    className={`text-center p-3 rounded-lg transition-colors ${
                      isCompleted ? 'bg-green-50 border border-green-200' :
                      isCurrent ? 'bg-blue-50 border-2 border-blue-400' :
                      'bg-slate-50 border border-slate-200'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center ${
                      isCompleted ? 'bg-green-500' :
                      isCurrent ? 'bg-blue-500' :
                      'bg-slate-300'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5 text-white" />
                      ) : (
                        <Icon className={`w-5 h-5 ${isCurrent ? 'text-white' : 'text-slate-500'}`} />
                      )}
                    </div>
                    <p className={`text-xs font-medium ${
                      isCompleted ? 'text-green-700' :
                      isCurrent ? 'text-blue-700' :
                      'text-slate-500'
                    }`}>
                      {stage.label}
                    </p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Profile Completion Form - shows when profile not yet complete */}
        {!candidate.profile_completed && (
          <ProfileCompletionForm
            candidate={candidate}
            onComplete={fetchApplication}
          />
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Documents Section */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-600" />
                    Your Documents
                  </CardTitle>
                  <CardDescription>{documents?.length || 0} documents uploaded</CardDescription>
                </div>
                {candidate.profile_completed ? (
                  <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid="upload-document-btn">
                        <Upload className="w-4 h-4 mr-2" />
                        Upload
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Upload Document</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label>Document Category</Label>
                          <Select value={uploadCategory} onValueChange={(value) => {
                            setUploadCategory(value);
                            if (value !== "Other Supporting Evidence") {
                              setCustomCategoryText("");
                            }
                          }}>
                            <SelectTrigger data-testid="upload-category-select">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                              {DOCUMENT_CATEGORIES.map((cat) => (
                                <SelectItem key={cat} value={cat}>
                                  {cat}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {uploadCategory === "Other Supporting Evidence" && (
                          <div className="space-y-2">
                            <Label>Specify Document Type <span className="text-red-500">*</span></Label>
                            <Input
                              value={customCategoryText}
                              onChange={(e) => setCustomCategoryText(e.target.value)}
                              placeholder="e.g., Training Certificate"
                              data-testid="custom-category-input"
                            />
                          </div>
                        )}
                        
                        <div className="space-y-2">
                          <Label>Select File</Label>
                          <Input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                            data-testid="file-input"
                            disabled={uploading || !uploadCategory || (uploadCategory === "Other Supporting Evidence" && !customCategoryText.trim())}
                          />
                          <p className="text-xs text-slate-500">
                            Supported: PDF (recommended), DOC, DOCX, JPG, PNG, TXT
                          </p>
                          <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-2 py-1.5">
                            💡 <strong>PDF is preferred.</strong> PDFs and images are watermarked with the viewer's identity when downloaded. DOC/DOCX/TXT files cannot be watermarked — convert to PDF for stronger security.
                          </p>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                ) : (
                  <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full border border-amber-200" data-testid="upload-locked-badge">
                    <Lock className="w-3.5 h-3.5" />
                    Complete profile first
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!candidate.profile_completed && (!documents || documents.length === 0) ? (
                <div className="text-center py-8" data-testid="upload-locked-message">
                  <Lock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">Document uploads locked</p>
                  <p className="text-sm text-slate-400 mt-1">Complete all 5 profile sections above to unlock</p>
                </div>
              ) : documents && documents.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {documents.map((doc) => {
                    const canDelete = ["draft", "documents_uploaded"].includes(candidate.case_status);
                    return (
                      <div 
                        key={doc.document_id}
                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                        data-testid={`doc-row-${doc.document_id}`}
                      >
                        <FileText className="w-8 h-8 text-slate-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">{doc.file_name}</p>
                          <p className="text-xs text-slate-500">
                            {doc.category}
                            {doc.custom_category && (
                              <span className="text-amber-600"> - {doc.custom_category}</span>
                            )}
                          </p>
                        </div>
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2 flex-shrink-0"
                            onClick={() => handleDeleteDocument(doc.document_id, doc.file_name)}
                            data-testid={`delete-doc-${doc.document_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Upload className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No documents uploaded yet</p>
                  <p className="text-sm text-slate-400">
                    {candidate.profile_completed ? "Upload your certificates and evidence" : "Complete your profile to start uploading"}
                  </p>
                </div>
              )}
              {candidate.case_status === "documents_uploaded" && documents?.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                  <Clock className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Documents received</p>
                    <p className="text-xs text-blue-600">Your documents will be reviewed and AI analysis will be conducted by the assessment team. You may continue uploading additional documents in the meantime.</p>
                  </div>
                </div>
              )}

              {/* In-app document checklist — same one as in the invitation email */}
              {candidate.profile_completed && (
                <details className="mt-4 group" data-testid="document-checklist">
                  <summary className="cursor-pointer text-sm font-medium text-slate-700 hover:text-slate-900 select-none flex items-center gap-2 py-2 px-3 bg-slate-50 rounded-lg border border-slate-200">
                    <FileText className="w-4 h-4 text-slate-500" />
                    Document checklist — what to upload (click to expand)
                  </summary>
                  <div className="mt-3 space-y-3 text-xs">
                    <p className="text-slate-600">
                      Accepted: <strong>PDF</strong> (preferred), DOC, DOCX, JPG, PNG. Max 25 MB per file. Use the suggested filename pattern so our AI can categorise your evidence accurately.
                    </p>
                    <div className="p-3 rounded-md bg-amber-50 border-l-4 border-amber-400">
                      <p className="font-semibold text-amber-900 mb-1">A. Identity & contact</p>
                      <ul className="list-disc list-inside space-y-1 text-amber-900">
                        <li>Government ID (NRIC / Passport / Driver's licence) — <code>ID_&lt;name&gt;.pdf</code></li>
                        <li>Proof of address (&lt; 3 months old) — <code>ProofOfAddress_&lt;name&gt;.pdf</code></li>
                        <li>Recent passport-sized photo — <code>Photo_&lt;name&gt;.jpg</code></li>
                      </ul>
                    </div>
                    <div className="p-3 rounded-md bg-emerald-50 border-l-4 border-emerald-500">
                      <p className="font-semibold text-emerald-900 mb-1">B. Education & qualifications</p>
                      <ul className="list-disc list-inside space-y-1 text-emerald-900">
                        <li>Full CV / Resume — <code>CV_&lt;name&gt;.pdf</code></li>
                        <li>Academic certificates (Diploma → PhD) — <code>Cert_&lt;qual&gt;_&lt;year&gt;.pdf</code></li>
                        <li>Academic transcripts — <code>Transcript_&lt;qual&gt;_&lt;year&gt;.pdf</code></li>
                        <li>Professional certifications & licences — <code>ProCert_&lt;name&gt;_&lt;year&gt;.pdf</code></li>
                      </ul>
                    </div>
                    <div className="p-3 rounded-md bg-blue-50 border-l-4 border-blue-500">
                      <p className="font-semibold text-blue-900 mb-1">C. Work experience & evidence</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-900">
                        <li>Employment / service letters — <code>EmploymentLetter_&lt;company&gt;_&lt;year&gt;.pdf</code></li>
                        <li>Job descriptions / scope of work — <code>JobDescription_&lt;role&gt;.pdf</code></li>
                        <li>Performance reviews / appraisals — <code>Appraisal_&lt;company&gt;_&lt;year&gt;.pdf</code></li>
                        <li>Reference letters from supervisors / industry peers — <code>Reference_&lt;referee&gt;.pdf</code></li>
                      </ul>
                    </div>
                    <div className="p-3 rounded-md bg-purple-50 border-l-4 border-purple-500">
                      <p className="font-semibold text-purple-900 mb-1">D. Awards & achievements (optional but recommended)</p>
                      <ul className="list-disc list-inside space-y-1 text-purple-900">
                        <li>Awards, recognitions, citations — <code>Award_&lt;title&gt;_&lt;year&gt;.pdf</code></li>
                        <li>Publications, articles, patents — <code>Publication_&lt;title&gt;_&lt;year&gt;.pdf</code></li>
                        <li>Project portfolios / work samples — <code>Portfolio_&lt;project&gt;.pdf</code></li>
                        <li>CPD / training attendance — <code>CPD_&lt;event&gt;_&lt;year&gt;.pdf</code></li>
                        <li>Professional body memberships — <code>Membership_&lt;body&gt;.pdf</code></li>
                      </ul>
                    </div>
                    <p className="text-slate-500 italic">
                      Tip: combine related documents into a single PDF (e.g. all transcripts together). Searchable PDFs help our AI produce a stronger qualification recommendation.
                    </p>
                  </div>
                </details>
              )}
            </CardContent>
          </Card>

          {/* Application Details */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-emerald-600" />
                Application Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500">Full Name</p>
                  <p className="font-medium">{candidate.full_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="font-medium">{candidate.email}</p>
                </div>
                {candidate.current_role && (
                  <div>
                    <p className="text-xs text-slate-500">Current Role</p>
                    <p className="font-medium">{candidate.current_role}</p>
                  </div>
                )}
                {candidate.current_employer && (
                  <div>
                    <p className="text-xs text-slate-500">Employer</p>
                    <p className="font-medium">{candidate.current_employer}</p>
                  </div>
                )}
              </div>
              
              <hr className="border-slate-200" />
              
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Institution</p>
                  <p className="font-medium">{institution?.name || "Not assigned"}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Award className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">IPCB Board</p>
                  <p className="font-medium">{ipcb?.name || "Not assigned"}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Application Date</p>
                  <p className="font-medium">{formatDate(candidate.created_at)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI Analysis Results (if available) */}
        {candidate.ai_recommendation && (
          <Card className="border-emerald-200 bg-emerald-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-emerald-600" />
                AI Analysis Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-xs text-slate-500">Recommended Level</p>
                  <p className="text-lg font-bold text-emerald-700 capitalize">
                    {candidate.ai_recommendation.recommended_level}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-xs text-slate-500">Primary Award</p>
                  <p className="font-medium text-slate-900">
                    {candidate.ai_recommendation.primary_award}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-xs text-slate-500">Evidence-Based Confidence</p>
                  <p className="text-lg font-bold text-blue-600">
                    {candidate.confidence_data?.calculated ?? candidate.ai_recommendation.confidence_score}%
                  </p>
                  {candidate.confidence_data && (
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      AI reported: {candidate.ai_recommendation.confidence_score}%
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending Information Requests from Admin/Assessor */}
        {candidate.info_requests?.some(r => r.status === "pending") && (
          <Card className="border-amber-200 bg-amber-50" data-testid="pending-info-requests">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <AlertCircle className="w-5 h-5" />
                Action Required — Additional Information Needed
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {candidate.info_requests.filter(r => r.status === "pending").map((req) => (
                <div key={req.request_id} className="p-4 bg-white rounded-lg border border-amber-200">
                  <p className="text-sm font-medium text-slate-800 mb-2">{req.message}</p>
                  {req.missing_items?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-amber-700 mb-1">Specific items needed:</p>
                      <ul className="text-sm text-slate-600 list-disc list-inside space-y-0.5">
                        {req.missing_items.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Type your response... Explain what documents you've uploaded or provide the requested information."
                      value={infoResponseText[req.request_id] || ""}
                      onChange={(e) => setInfoResponseText({ ...infoResponseText, [req.request_id]: e.target.value })}
                      rows={3}
                      data-testid={`info-response-${req.request_id}`}
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        onClick={() => handleRespondToInfoRequest(req.request_id)}
                        disabled={submittingResponse === req.request_id}
                        data-testid={`submit-response-${req.request_id}`}
                      >
                        {submittingResponse === req.request_id ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : null}
                        Submit Response
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              <p className="text-xs text-amber-600">
                You can also upload additional documents above to support your response.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Gap Report for Candidate - shows what docs they need to upload */}
        {candidate.gap_report && candidate.gap_report.gaps_count > 0 && (
          <GapReportPanel gapReport={candidate.gap_report} isCandidate={true} />
        )}

        {/* Written Interview Section */}
        {candidate.written_interview && candidate.interview_mode === "written" && (
          <WrittenInterviewSection
            candidate={candidate}
            onRefresh={fetchApplication}
          />
        )}

        {/* Live Interview Section */}
        {candidate.interview_schedule && candidate.interview_mode !== "written" && (candidate.case_status === 'interview_scheduled' || candidate.case_status === 'interview_pending' || candidate.case_status === 'interview_failed') && (
          <Card className="border-purple-200 bg-purple-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-700">
                <Video className="w-5 h-5" />
                Interview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Select Time Slot */}
              {candidate.interview_schedule && !candidate.interview_schedule.selected_slot && (
                <div className="bg-white p-4 rounded-lg border border-purple-200">
                  <h4 className="font-medium text-slate-900 mb-3">Select Your Interview Time</h4>
                  <p className="text-sm text-slate-600 mb-4">Please choose one of the following time slots:</p>
                  <div className="space-y-2">
                    {['1', '2', '3'].map((slot) => (
                      candidate.interview_schedule[`slot_${slot}`] && (
                        <Button
                          key={slot}
                          variant="outline"
                          className="w-full justify-start"
                          disabled={selectingSlot}
                          onClick={() => handleSelectInterviewSlot(slot)}
                        >
                          <Calendar className="w-4 h-4 mr-2" />
                          {candidate.interview_schedule[`slot_${slot}`]}
                        </Button>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* Confirmed Slot */}
              {candidate.interview_schedule?.selected_datetime && (
                <div className="bg-white p-4 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 text-green-700 mb-2">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Interview Confirmed</span>
                  </div>
                  <p className="text-slate-900 font-medium">{candidate.interview_schedule.selected_datetime}</p>
                  {candidate.interview_schedule.meeting_link && (
                    <a 
                      href={candidate.interview_schedule.meeting_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      <Video className="w-4 h-4" />
                      Join Meeting
                    </a>
                  )}
                </div>
              )}

              {/* Interview Results */}
              {candidate.interview_results && (
                <div className={`p-4 rounded-lg border ${
                  candidate.interview_results.overall_result === 'passed' 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {candidate.interview_results.overall_result === 'passed' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span className="font-medium">
                      Interview {candidate.interview_results.overall_result === 'passed' ? 'Passed' : 'Not Passed'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600">
                    {candidate.interview_results.passed_count} of {candidate.interview_results.total_questions} questions passed
                  </p>
                </div>
              )}

              {/* Appeal Option */}
              {candidate.case_status === 'interview_failed' && !candidate.appeal && (
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                  <h4 className="font-medium text-amber-800 mb-2">Want to Appeal?</h4>
                  <p className="text-sm text-amber-700 mb-3">
                    You can submit an appeal with your statement. If approved, you will be scheduled for a re-interview.
                  </p>
                  <Dialog open={appealDialogOpen} onOpenChange={setAppealDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-100">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Submit Appeal
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Submit Appeal</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>Your Statement</Label>
                          <Textarea
                            placeholder="Explain why you believe you should be given another opportunity..."
                            value={appealRemarks}
                            onChange={(e) => setAppealRemarks(e.target.value)}
                            rows={5}
                          />
                        </div>
                        <div className="flex justify-end gap-3">
                          <Button variant="outline" onClick={() => setAppealDialogOpen(false)}>Cancel</Button>
                          <Button onClick={handleSubmitAppeal} disabled={submittingAppeal}>
                            {submittingAppeal ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Submitting...
                              </>
                            ) : (
                              'Submit Appeal'
                            )}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}

              {/* Appeal Pending */}
              {candidate.appeal?.status === 'pending' && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-700 mb-2">
                    <Clock className="w-5 h-5" />
                    <span className="font-medium">Appeal Under Review</span>
                  </div>
                  <p className="text-sm text-blue-600">
                    Your appeal has been submitted and is being reviewed by the assessor.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Final Decision (if approved/report generated) */}
        {["approved", "report_generated", "closed"].includes(candidate.case_status) && (
          <Card className="border-green-300 bg-gradient-to-r from-green-50 to-emerald-50" data-testid="approved-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-6 h-6" />
                Congratulations! Your Assessment is Complete
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {candidate.final_award ? (
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-green-600 mb-1">Awarded Qualification</p>
                    <p className="text-xl font-bold text-green-800">{candidate.final_award}</p>
                  </div>
                  <div>
                    <p className="text-sm text-green-600 mb-1">Total Credits</p>
                    <p className="text-xl font-bold text-green-800">{candidate.final_credits || "—"} Credits</p>
                  </div>
                </div>
              ) : candidate.ai_recommendation ? (
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-green-600 mb-1">Recommended Award</p>
                    <p className="text-xl font-bold text-green-800">{candidate.ai_recommendation.primary_award}</p>
                  </div>
                  <div>
                    <p className="text-sm text-green-600 mb-1">Recommended Level</p>
                    <p className="text-xl font-bold text-green-800 capitalize">{candidate.ai_recommendation.recommended_level}</p>
                  </div>
                </div>
              ) : null}
              
              {candidate.case_status === "report_generated" && (
                <div className="bg-white rounded-lg border border-green-200 p-4 flex items-center gap-3">
                  <FileCheck className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Reports have been generated</p>
                    <p className="text-xs text-green-600">Your IPCB and Institution reports are ready. Use the credential card below to share or verify.</p>
                  </div>
                </div>
              )}
              
              <p className="text-sm text-green-700">
                Your official reports are available. Please contact your institution to collect your certificate.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Verifiable Credential card (Report IDs + QR + Share) */}
        {["approved", "report_generated", "closed"].includes(candidate.case_status) &&
          (candidate.ipcb_report_id || candidate.institution_report_id) && (
            <CredentialCard candidate={candidate} />
          )}

        {/* Help Card */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-slate-900">Need Help?</h4>
                <p className="text-sm text-slate-600 mt-1">
                  If you have questions about your assessment, please contact your institution's RPL coordinator or email support.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
